/*
 * ReportMetricsCheck
 * The reader ticks which metrics their current SEO report includes.
 *
 * Rules:
 * - Each metric is tagged "cases" (ties back to calls, consultations or signed cases) or
 *   "activity" (shows activity or visibility but not cases).
 * - Results group the ticked metrics into those two lists.
 * - Every "cases" metric NOT ticked is listed under "Ask your agency to add", with where the
 *   data comes from.
 * - If nothing "cases"-type is ticked but at least one "activity" metric is, the summary says the
 *   report can't show whether SEO is producing cases.
 * - Results appear once at least one metric is ticked.
 * No scores, benchmarks, tracking, storage or network calls.
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";

type Kind = "cases" | "activity";
type Metric = { id: string; label: string; kind: Kind; source?: string };

const METRICS: Metric[] = [
  { id: "impressions", label: "Impressions", kind: "activity" },
  { id: "score", label: "An “SEO score” or “health score”", kind: "activity" },
  { id: "keywords", label: "Number of keywords tracked or ranking", kind: "activity" },
  { id: "traffic", label: "Total website traffic", kind: "activity" },
  { id: "backlinks", label: "Number of backlinks built", kind: "activity" },
  { id: "calls", label: "Phone calls from search and the business profile", kind: "cases", source: "Call tracking and Google Business Profile performance" },
  { id: "directions", label: "Direction requests and profile views", kind: "cases", source: "Google Business Profile performance" },
  { id: "forms", label: "Form submissions from organic search", kind: "cases", source: "GA4 key events filtered to organic search" },
  { id: "pages", label: "Which pages those calls and forms came from", kind: "cases", source: "GA4 landing pages with key events" },
  { id: "consults", label: "Consultations booked from organic", kind: "cases", source: "Your intake records, matched to the lead source" },
  { id: "signed", label: "Signed cases from organic", kind: "cases", source: "Your case management system or CRM" },
];

export default function ReportMetricsCheck() {
  const [ticked, setTicked] = useState<Record<string, boolean>>({});

  const chosen = METRICS.filter((m) => ticked[m.id]);
  const casesIn = chosen.filter((m) => m.kind === "cases");
  const activityIn = chosen.filter((m) => m.kind === "activity");
  const toAdd = METRICS.filter((m) => m.kind === "cases" && !ticked[m.id]);

  const focusRing =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  let summary = "";
  if (chosen.length > 0) {
    if (casesIn.length === 0) summary = "This report shows activity, but it can't tell you whether SEO is producing cases.";
    else if (toAdd.length === 0) summary = "Your report tracks the full path from search to signed case.";
    else summary = `Your report covers part of the path to a signed case. ${toAdd.length} metric${toAdd.length === 1 ? " is" : "s are"} missing.`;
  }

  return (
    <section className="not-prose my-8 rounded-xl border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">What does your SEO report actually measure?</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Open your latest agency report and tick everything it includes. Nothing is saved or sent.
      </p>

      <fieldset className="mt-5">
        <legend className="sr-only">Metrics in your report</legend>
        <ul className="grid gap-2 sm:grid-cols-2">
          {METRICS.map((m) => {
            const pressed = !!ticked[m.id];
            return (
              <li key={m.id}>
                <Button variant="outline"
                  type="button"
                  aria-pressed={pressed}
                  onClick={() => setTicked((p) => ({ ...p, [m.id]: !p[m.id] }))}
                  className={`h-auto w-full justify-start whitespace-normal rounded-lg border px-3 py-2 text-left text-sm ${focusRing} ${
                    pressed ? "border-border bg-primary text-primary-foreground" : "border-input bg-card text-card-foreground"
                  }`}
                >
                  <span aria-hidden="true">{pressed ? "✓ " : "○ "}</span>
                  {m.label}
                </Button>
              </li>
            );
          })}
        </ul>
      </fieldset>

      <div aria-live="polite" className="mt-5 rounded-lg bg-muted p-4">
        {chosen.length === 0 ? (
          <p className="text-sm text-muted-foreground">Tick the metrics in your report to see what it can and can't tell you.</p>
        ) : (
          <div>
            <p className="text-sm font-semibold">{summary}</p>

            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div className="rounded-md border border-border bg-card p-3 text-sm">
                <p className="font-medium">Tells you about cases</p>
                {casesIn.length === 0 ? (
                  <p className="mt-1 text-muted-foreground">None</p>
                ) : (
                  <ul className="mt-1 list-disc pl-5">
                    {casesIn.map((m) => (
                      <li key={m.id}>{m.label}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="rounded-md border border-border bg-card p-3 text-sm">
                <p className="font-medium">Shows activity, not cases</p>
                {activityIn.length === 0 ? (
                  <p className="mt-1 text-muted-foreground">None</p>
                ) : (
                  <ul className="mt-1 list-disc pl-5">
                    {activityIn.map((m) => (
                      <li key={m.id}>{m.label}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {toAdd.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-semibold">Ask your agency to add</p>
                <ul className="mt-2 space-y-2">
                  {toAdd.map((m) => (
                    <li key={m.id} className="rounded-md border border-border bg-card p-3 text-sm">
                      <p className="font-medium">{m.label}</p>
                      <p className="mt-1 text-muted-foreground">Comes from: {m.source}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="mt-4 text-sm">
              Need reporting that connects search to cases?{" "}
              <Link to="/services/$slug" params={{ slug: "law-firm-marketing-analytics" }} className={`font-medium underline underline-offset-4 ${focusRing}`}>
                Analytics &amp; reporting →
              </Link>
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
